import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Gem, Users, Radio, Eye, ArrowRight, RefreshCw, Loader2, AlertCircle,
    TrendingUp, TrendingDown, Package, Clock, ShoppingCart, Activity,
} from "lucide-react";
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChannelLink } from "@/components/ChannelLink";
import { premiumChannel } from "@/lib/api";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const shortDate = (ts) => {
    if (!ts) return "";
    try { return new Date(ts).toLocaleDateString("en-CA").slice(5); } catch { return String(ts).slice(5, 10); }
};

function Kpi({ label, value, sub, icon: Icon, color = "cyan", testId }) {
    const ring = {
        cyan: "ring-cyan-500/20 text-cyan-400",
        emerald: "ring-emerald-500/20 text-emerald-400",
        amber: "ring-amber-500/20 text-amber-400",
        rose: "ring-rose-500/20 text-rose-400",
        violet: "ring-violet-500/20 text-violet-400",
    }[color];
    return (
        <Card className={`bg-card/50 ring-1 ${ring}`} data-testid={testId}>
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    {Icon && <Icon className="h-4 w-4 opacity-50" />}
                </div>
                <p className="text-2xl font-bold mt-1" dir="ltr">{typeof value === "number" ? fmt(value) : value}</p>
                {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
            </CardContent>
        </Card>
    );
}

const tooltipStyle = {
    background: "hsl(210 18% 9%)",
    border: "1px solid hsl(210 14% 18%)",
    borderRadius: "0.5rem",
    fontSize: "12px",
};

export default function PremiumChannelDetail() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await premiumChannel(chatId, 30);
            setData(d);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403) setError("هذه الصفحة مخصّصة لمالك المنصّة فقط.");
            else if (status === 404) setError("القناة غير موجودة.");
            else setError(e?.response?.data?.detail || "تعذّر تحميل تفاصيل البريميوم.");
        }
        setLoading(false);
    }, [chatId]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="space-y-4" data-testid="premium-detail-loading">
                <Skeleton className="h-16 w-full" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-72 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="premium-detail-error">
                <Card className="bg-card/60 border-border/40 max-w-md w-full">
                    <CardContent className="p-8 text-center space-y-3">
                        <AlertCircle className="h-10 w-10 mx-auto text-rose-400" />
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <div className="flex justify-center gap-2">
                            <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> إعادة</Button>
                            <Button variant="ghost" onClick={() => navigate("/premium")}>رجوع</Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const countedSeries = (data.counted_series || []).map((s) => ({ ...s, label: shortDate(s.ts) }));
    const growthSeries = (data.growth_series || []).map((s) => ({ ...s, label: shortDate(s.ts) }));
    const flow = data.flow || [];

    return (
        <div className="space-y-5 pb-10" data-testid="premium-detail-page">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/premium")} data-testid="premium-detail-back">
                        <ArrowRight className="h-5 w-5" />
                    </Button>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 shrink-0">
                        <Gem className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-lg font-bold">
                            <ChannelLink title={data.title} username={data.username} chatId={data.chat_id} showUsername={false} testId="premium-detail-channel" />
                        </div>
                        {data.username && <p className="text-xs text-muted-foreground font-mono" dir="ltr">@{data.username}</p>}
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="premium-detail-refresh">
                    <RefreshCw className="h-4 w-4" /> تحديث
                </Button>
            </div>

            {!data.reliable && (
                <Card className="bg-amber-500/5 border-amber-500/20">
                    <CardContent className="p-3 text-xs text-amber-300 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        التتبّع «قيد البناء» لهذه القناة — لم يكتمل رصد كل المميزين القدامى بعد، لذا الفجوة ونسبة التفعيل قد لا تكون دقيقة حتى الآن.
                    </CardContent>
                </Card>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="محتسب (Boost) 💎" value={data.counted} sub="ما يحتسبه تلغرام" icon={Gem} color="amber" testId="kpi-d-counted" />
                <Kpi label="حاضر فعلاً" value={data.present_premium} sub={`من ${fmt(data.tracked_total)} متتبَّع`} icon={Users} color="cyan" testId="kpi-d-present" />
                <Kpi label="الأعضاء" value={data.members} icon={Radio} color="violet" testId="kpi-d-members" />
                <Kpi label="غادر (مميز)" value={data.left_premium} icon={TrendingDown} color="rose" testId="kpi-d-left" />
                <Kpi label="نسبة التفعيل" value={data.active_rate != null ? `${data.active_rate}%` : "—"} sub="محتسب ÷ حاضر" icon={Activity} color="emerald" testId="kpi-d-rate" />
                <Kpi label="الفجوة" value={data.gap != null ? data.gap : "قيد البناء"} sub="حاضر − محتسب" icon={TrendingUp} color="amber" testId="kpi-d-gap" />
                <Kpi label="الجمهور النشط 👁" value={data.active_audience} sub={data.active_ratio != null ? `${data.active_ratio}% نشاط` : null} icon={Eye} color="cyan" testId="kpi-d-active" />
                <Kpi label="المتوقّع احتسابه" value={data.expected_counted} sub="حاضر × النشاط" icon={Gem} color="emerald" testId="kpi-d-expected" />
            </div>

            {/* Since-update */}
            <Card className="bg-card/40 border-border/40">
                <CardContent className="p-4 flex items-center gap-6 flex-wrap text-sm">
                    <span className="text-muted-foreground">منذ آخر تحديث ترتيب:</span>
                    <span className="inline-flex items-center gap-1 text-emerald-300"><TrendingUp className="h-4 w-4" /> انضمّ {fmt(data.joined_since_update)} مميز</span>
                    <span className="inline-flex items-center gap-1 text-rose-300"><TrendingDown className="h-4 w-4" /> غادر {fmt(data.left_since_update)} مميز</span>
                    {data.reactions_per_post != null && <span className="text-muted-foreground">تفاعل/منشور: <b className="text-foreground" dir="ltr">{fmt(data.reactions_per_post)}</b></span>}
                    {data.avg_recent_views != null && <span className="text-muted-foreground">متوسط مشاهدات حديثة: <b className="text-foreground" dir="ltr">{fmt(data.avg_recent_views)}</b></span>}
                </CardContent>
            </Card>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-card/40 border-border/40">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Gem className="h-4 w-4 text-amber-400" /> المميزون المحتسبون عبر الزمن</CardTitle></CardHeader>
                    <CardContent>
                        {countedSeries.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-12">لا توجد بيانات كافية بعد.</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={countedSeries} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(210 14% 18%)" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                    <YAxis tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: "12px" }} />
                                    <Line type="monotone" dataKey="premium" name="محتسب 💎" stroke="#fbbf24" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card className="bg-card/40 border-border/40">
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Radio className="h-4 w-4 text-cyan-400" /> نموّ الأعضاء</CardTitle></CardHeader>
                    <CardContent>
                        {growthSeries.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-12">لا توجد بيانات كافية بعد.</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={growthSeries} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(210 14% 18%)" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                    <YAxis tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Line type="monotone" dataKey="members" name="الأعضاء" stroke="#22d3ee" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Premium flow */}
            <Card className="bg-card/40 border-border/40">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-emerald-400" /> تدفّق المميزين اليومي (انضمام / مغادرة)</CardTitle></CardHeader>
                <CardContent>
                    {flow.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-12">لا يوجد نشاط مميزين خلال الفترة.</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={flow} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(210 14% 18%)" />
                                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                <YAxis tick={{ fontSize: 11, fill: "hsl(215 12% 70%)" }} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: "12px" }} />
                                <Bar dataKey="joins" name="انضمام" fill="#34d399" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="leaves" name="مغادرة" fill="#fb7185" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* Channel batches */}
            <Card className="bg-card/40 border-border/40">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-amber-400" /> دفعات شراء هذه القناة
                        <Badge variant="outline" className="border-border/40 text-muted-foreground text-xs" dir="ltr">{(data.batches || []).length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table data-testid="premium-detail-batches">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="text-center whitespace-nowrap">التاريخ</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">شراء 🛒</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">متبقّي</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">غادر</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">عبر رابط</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">الحالة / التفعيل</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(!data.batches || data.batches.length === 0) && (
                                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">لا توجد دفعات مكتشفة لهذه القناة.</TableCell></TableRow>
                                )}
                                {(data.batches || []).map((b, i) => (
                                    <TableRow key={i} data-testid={`detail-batch-${i}`}>
                                        <TableCell className="text-center text-muted-foreground font-mono text-xs whitespace-nowrap" dir="ltr">{(b.start || "").slice(0, 10)}</TableCell>
                                        <TableCell className="text-center font-bold text-amber-300" dir="ltr">{fmt(b.size_premium)}</TableCell>
                                        <TableCell className="text-center text-cyan-300" dir="ltr">{fmt(b.present_premium)}</TableCell>
                                        <TableCell className="text-center" dir="ltr">{b.left_premium ? <span className="text-rose-300">{fmt(b.left_premium)}</span> : <span className="text-muted-foreground">0</span>}</TableCell>
                                        <TableCell className="text-center text-muted-foreground" dir="ltr">{Math.round((b.invite_ratio || 0) * 100)}%</TableCell>
                                        <TableCell className="text-center">
                                            {b.status === "measured" ? (
                                                <div className="flex flex-col items-center gap-1 min-w-[120px]">
                                                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-xs" dir="ltr">✅ {b.activation_pct}% ({fmt(b.counted_delta)})</Badge>
                                                    <Progress value={Math.min(100, b.activation_pct || 0)} className="h-1.5 w-full" />
                                                </div>
                                            ) : (
                                                <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-xs"><Clock className="h-3 w-3 mr-1" />بانتظار التحديث</Badge>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
